import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePharmacyUsers } from '../../../../../../hooks/queries/usePharmacyUsers';
import { taskQueueCountsQueryKey } from '../../../../../../hooks/queries/useTaskQueueCounts';
import { createManualTask, type CreateManualTaskDto } from '../../../../../../services/taskQueueService';
import {
  TASK_DEPARTMENT_LABELS,
  TASK_DEPARTMENT_VALUES,
  type TaskDepartment,
} from '../../../../../../constants/taskDepartments';
import { getDepartmentAssignableUsers } from '../../../../../../utils/assignableUsers';

interface VacationSupplyRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** The patient's pharmacy: the task is created there and its staff are offered. */
  pharmacyId: string;
  /** The task for the picked department and assignee. */
  buildTask: (department: TaskDepartment, assignedTo: string | null) => CreateManualTaskDto;
  onCreated: (title: string) => void;
}

const UNASSIGNED = '';

/**
 * PRX-130 — pick the department and assignee for a vacation supply request,
 * see the task body, and create it through the manual-tasks API (PRX-67). The
 * server re-checks the assignee against the department.
 */
export const VacationSupplyRequestDialog: React.FC<VacationSupplyRequestDialogProps> = ({
  open,
  onClose,
  pharmacyId,
  buildTask,
  onCreated,
}) => {
  const queryClient = useQueryClient();
  const [department, setDepartment] = useState<TaskDepartment | ''>('');
  const [assignedTo, setAssignedTo] = useState<string>(UNASSIGNED);
  const users = usePharmacyUsers(pharmacyId);
  const options = useMemo(
    () => (department ? getDepartmentAssignableUsers(users.data?.users, department) : []),
    [users.data?.users, department],
  );

  const create = useMutation({
    mutationFn: (dto: CreateManualTaskDto) => createManualTask(pharmacyId, dto),
    onSuccess: (_result, dto) => {
      queryClient.invalidateQueries({ queryKey: taskQueueCountsQueryKey(pharmacyId) });
      onCreated(dto.title);
    },
  });
  const { reset } = create;

  // Each opening starts clean: no department, no assignee, no last outcome.
  useEffect(() => {
    if (open) {
      setDepartment('');
      setAssignedTo(UNASSIGNED);
      reset();
    }
  }, [open, reset]);

  const task = department ? buildTask(department, assignedTo || null) : null;
  const serverMessage: unknown = (create.error as any)?.response?.data?.message;
  const errorMessage = !create.error
    ? null
    : Array.isArray(serverMessage)
      ? serverMessage.join('; ')
      : typeof serverMessage === 'string' && serverMessage
        ? serverMessage
        : "Couldn't create the task";

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create vacation supply request</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This creates a task in the Task Queue. Nothing is billed and nothing is written to the pharmacy system.
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id="vacation-request-department">Department</InputLabel>
            <Select
              labelId="vacation-request-department"
              label="Department"
              value={department}
              onChange={(e) => {
                setDepartment(e.target.value as TaskDepartment);
                setAssignedTo(UNASSIGNED);
              }}
            >
              {TASK_DEPARTMENT_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {TASK_DEPARTMENT_LABELS[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!department}>
            <InputLabel id="vacation-request-assignee" shrink>
              Assignee
            </InputLabel>
            <Select
              labelId="vacation-request-assignee"
              label="Assignee"
              value={assignedTo}
              displayEmpty
              notched
              onChange={(e) => setAssignedTo(String(e.target.value))}
            >
              <MenuItem value={UNASSIGNED}>Unassigned</MenuItem>
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {users.isError && (
            <Alert severity="warning">Couldn't read the pharmacy's staff. The task can be created unassigned.</Alert>
          )}
          {task && (
            <Box>
              <Typography variant="subtitle2">{task.title}</Typography>
              <Box
                component="pre"
                data-testid="vacation-request-body"
                sx={{
                  m: 0,
                  mt: 1,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  typography: 'body2',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 280,
                  overflow: 'auto',
                }}
              >
                {task.description}
              </Box>
            </Box>
          )}
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!task || create.isPending}
          onClick={() => task && create.mutate(task)}
        >
          {create.isPending ? 'Creating' : 'Create task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VacationSupplyRequestDialog;
